import { useState, useEffect } from 'react'
import { MapPin, Globe, AlertCircle, Building2 } from 'lucide-react'
import { SearchBox } from '@mapbox/search-js-react'
import type { BusinessScope } from '../../types/brand'
import {
  BUSINESS_SCOPE_LABELS,
  BUSINESS_SCOPE_DESCRIPTIONS,
  formatLocation
} from '../../types/brand'

interface BrandConfirmationStepProps {
  brandName: string
  brandDescription: string
  initialScope?: BusinessScope
  initialServiceAreas?: string[]
  initialPrimaryLocation?: string
  onSave: (data: {
    companyName: string
    description: string
    businessScope: BusinessScope
    serviceAreas: string[]
    primaryLocation?: string
  }) => void
  isProcessing?: boolean
}

const SCOPE_OPTIONS: BusinessScope[] = ['LOCAL', 'NATIONAL']

export default function BrandConfirmationStep({
  brandName,
  brandDescription,
  initialScope = 'NATIONAL',
  initialServiceAreas = [],
  initialPrimaryLocation,
  onSave,
  isProcessing = false
}: BrandConfirmationStepProps) {
  // Brand info state
  const [companyName, setCompanyName] = useState(brandName)
  const [description, setDescription] = useState(brandDescription)

  // Business scope state
  const [selectedScope, setSelectedScope] = useState<BusinessScope>(initialScope)
  const [serviceAreas, setServiceAreas] = useState<string[]>(initialServiceAreas)
  const [primaryLocation, setPrimaryLocation] = useState(initialPrimaryLocation || '')
  const [currentLocationInput, setCurrentLocationInput] = useState(initialPrimaryLocation || '')
  const [locationError, setLocationError] = useState('')

  // Validation state
  const [nameError, setNameError] = useState('')
  const [descriptionError, setDescriptionError] = useState('')

  // Update local state when props change (profile loaded)
  useEffect(() => {
    if (brandName) setCompanyName(brandName)
    if (brandDescription) setDescription(brandDescription)
  }, [brandName, brandDescription])

  // Reset location fields when scope changes
  useEffect(() => {
    if (selectedScope === 'NATIONAL') {
      setServiceAreas([])
      setPrimaryLocation('')
      setCurrentLocationInput('')
      setLocationError('')
    }
  }, [selectedScope])

  const needsLocationInput = selectedScope === 'LOCAL'

  const handleSearchBoxRetrieve = (result: any) => {
    const feature = result.features?.[0]
    if (!feature) return

    const placeName = feature.properties?.name || feature.text || ''
    const context = feature.properties?.context || feature.context || {}

    let region = context.region?.region_code || context.region?.name || ''
    if (region.startsWith('US-')) {
      region = region.replace('US-', '')
    }

    if (placeName && region) {
      const locationString = `${placeName}, ${region}`
      const formatted = formatLocation(locationString)

      setCurrentLocationInput(locationString)
      setServiceAreas([formatted])
      setPrimaryLocation(formatted)
      setLocationError('')
    }
  }

  const handleSave = () => {
    // Clear previous errors
    setNameError('')
    setDescriptionError('')
    setLocationError('')

    // Validate brand name
    if (!companyName.trim()) {
      setNameError('Brand name is required')
      return
    }

    // Validate description
    if (!description.trim()) {
      setDescriptionError('Brand description is required')
      return
    }

    // Validate location for local businesses
    if (needsLocationInput && serviceAreas.length === 0) {
      setLocationError('Please add at least one location for your service area')
      return
    }

    onSave({
      companyName: companyName.trim(),
      description: description.trim(),
      businessScope: selectedScope,
      serviceAreas,
      primaryLocation: primaryLocation || undefined
    })
  }

  const isValid = companyName.trim() && description.trim() && (!needsLocationInput || serviceAreas.length > 0)

  return (
    <div className="space-y-6">
      {/* Brand Info Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2 mb-2">
          <Building2 className="h-5 w-5 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">Confirm Your Brand Details</h3>
        </div>

        {/* Brand Name */}
        <div>
          <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 mb-1">
            Brand Name
          </label>
          <input
            type="text"
            id="brandName"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value)
              setNameError('')
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
              nameError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Your company name"
          />
          {nameError && (
            <p className="mt-1 text-xs text-red-600">{nameError}</p>
          )}
        </div>

        {/* Brand Description */}
        <div>
          <label htmlFor="brandDescription" className="block text-sm font-medium text-gray-700 mb-1">
            Brand Description
          </label>
          <textarea
            id="brandDescription"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              setDescriptionError('')
            }}
            rows={3}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${
              descriptionError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Describe what your company does..."
          />
          {descriptionError && (
            <p className="mt-1 text-xs text-red-600">{descriptionError}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            This helps AI understand your brand and generate relevant search queries
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Business Scope Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What market area does your business primarily serve?
        </label>
        <div className="space-y-2">
          {SCOPE_OPTIONS.map(scope => {
            const isSelected = selectedScope === scope
            const needsInput = scope === 'LOCAL'

            return (
              <div
                key={scope}
                className={`border rounded-lg transition-all ${
                  isSelected
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Scope Header */}
                <div
                  onClick={() => setSelectedScope(scope)}
                  className="p-4 cursor-pointer"
                >
                  <div className="flex items-start space-x-3">
                    <div className="mt-0.5">
                      {scope === 'LOCAL' && <MapPin className="h-5 w-5 text-indigo-600" />}
                      {scope === 'NATIONAL' && <Globe className="h-5 w-5 text-indigo-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">
                          {BUSINESS_SCOPE_LABELS[scope]}
                        </p>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {BUSINESS_SCOPE_DESCRIPTIONS[scope]}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Location Input */}
                {isSelected && needsInput && (
                  <div className="px-4 pb-4 pt-2 border-t border-indigo-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary City
                      <span className="text-gray-500 text-xs ml-2">
                        (Including surrounding area)
                      </span>
                    </label>

                    <div className="space-y-2">
                      <SearchBox
                        accessToken={import.meta.env.VITE_MAPBOX_API_KEY}
                        value={currentLocationInput}
                        onChange={(value) => {
                          setCurrentLocationInput(value)
                          setLocationError('')
                        }}
                        onRetrieve={handleSearchBoxRetrieve}
                        placeholder="e.g., San Diego, CA"
                        options={{
                          language: 'en',
                          country: 'US',
                          types: 'place'
                        }}
                      />

                      {locationError && (
                        <div className="flex items-center space-x-2 text-red-600 text-xs">
                          <AlertCircle className="h-4 w-4" />
                          <span>{locationError}</span>
                        </div>
                      )}

                      <p className="text-xs text-gray-500">
                        Your primary city location - we'll automatically include the surrounding area
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">
              What happens next?
            </p>
            <p className="text-xs text-blue-700">
              We'll analyze your brand profile to identify your market industry and generate relevant search queries.
              This helps us tailor analysis to your specific market and business scope.
            </p>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={!isValid || isProcessing}
          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </div>
  )
}
